// Composer Studio — 레거시 join-by-key mergeFields의 **로드 시 자동 이행** (2026-07-26 사용자 확정)
//
// 접두 자동 노출 개편으로 엔진(assemblers.ts joinByKey)은 조인 소스의 모든 칼럼을
// `${inputs.join}.${칼럼}` flat 키로 자동 산출한다. 레거시 options.mergeFields(출력키 ← srcPath)의
// 출력값은 `getByPath(matched, srcPath) ?? emptyValue`라, srcPath가 **top-level(점 없음)이면**
// 접두 키 `matched[srcPath] ?? emptyValue`와 완전 동치다. 이 변환은 에디터 로드 시 그 동치
// 항목의 바인딩 키를 접두 키로 재작성한 뒤 mergeFields에서 제거한다 — 다음 저장 때 DB도 함께
// 정리되는 stripComputeDefFields(dataDefJoin.ts)와 같은 "로드 정규화" 패턴.
//
// ⚠️ 이행하면 outKey는 더 이상 조인 결과 행에 실리지 않는다. 따라서 def 행 스코프 안의
// outKey 참조는 **전부** rename되거나, 하나라도 등가성이 불확실하면 그 항목은 이행하지 않고
// 유지한다(레거시 경로가 계속 동작 — 요약 UI에 잔존 표시). 보류 사유:
//  - srcPath에 점(.)이 있음 — 접두 flat 키는 top-level 칼럼만 산출.
//  - format 문자열('{outKey}' / switch.on / unit.unitPath 등)의 경로 참조 — 자동 rewrite 금지
//    (renameDataSourceFieldEverywhere(rowsColumnOps.ts) 선례).
//  - def 스코프 안 중첩 행 스코프(내부 repeat / 반복영역 표) · 계산 셀 입력 · 집계 ·
//    모달 commit/바인딩 · writes 등 등가성 판단이 불확실한 소비 지점.
//
// rename 스코프는 "그 def를 소비하는 표면" 한정 — 같은 키 문자열(예: improvementDate)이 다른
// 스코프(다른 반복영역의 동명 행 필드)에 실존하므로 전역 문자열 치환은 절대 금지.

import type {
  Binding,
  BindingFormat,
  ComposerNode,
  ComposerWidget,
  DataDef,
  FormTableWidget,
  ReportTemplate,
  TableCell,
} from '@safetyhub/shared';
import { updateCell, updateNode } from '@safetyhub/report-composer';
import { isJoinByKeyDef, readJoinConfig, readMergeFieldEntries } from './dataDefJoin';

/**
 * format 정의가 key(스코프 경로)를 참조하면 true — 자동 rewrite 금지 대상.
 * template/join.itemTemplate/switch 케이스의 '{key}'·'{key.…}' 치환과, 경로 필드
 * (unit.unitPath / switch.on / rangeMap.rangesKey·annotationKey)의 key·'key.…' head를 본다.
 */
function formatReferencesKey(format: BindingFormat | undefined, key: string): boolean {
  if (!format) return false;
  const headIs = (p: string | undefined): boolean => !!p && (p === key || p.startsWith(`${key}.`));
  const tmplRefs = (s: string): boolean => s.includes(`{${key}}`) || s.includes(`{${key}.`);
  switch (format.kind) {
    case 'unit':
      return headIs(format.unitPath);
    case 'template':
      return tmplRefs(format.template);
    case 'join':
      return format.itemTemplate ? tmplRefs(format.itemTemplate) : false;
    case 'switch':
      return (
        headIs(format.on) ||
        Object.values(format.cases).some(tmplRefs) ||
        (format.default ? tmplRefs(format.default) : false)
      );
    case 'rangeMap':
      // 병기(annotationKey)도 rangesKey와 같은 스코프 경로다 — 한쪽만 보면 rename 후 병기가
      // 조용히 빈 값이 된다(빠진 축은 "아무것도 안 나옴"으로만 드러나 추적이 어렵다).
      return headIs(format.rangesKey) || headIs(format.annotationKey);
    default:
      return false;
  }
}

/** body 스캔 결과 — rename 좌표(적용은 updateCell/updateNode) + 이행 보류 여부. */
interface BodyScan {
  blocked: boolean;
  cellTargets: { tableId: string; row: number; col: number; binding: Binding }[];
  nodeTargets: { nodeId: string; binding: Binding }[];
}

/**
 * body에서 defKey 소비 표면의 outKey 참조를 수집한다.
 *  - 반복영역 표(region.over===defKey)의 직접 셀 바인딩 → rename 좌표
 *    (renameDataSourceFieldEverywhere b 패턴 미러 — 표 단위, 좌표 선수집 후 순차 적용).
 *  - RepeatNode(over===defKey) template 직속 스코프의 노드/셀 바인딩 → rename 좌표.
 *  - 그 외 발견(중첩 행 스코프·format 참조·계산 입력·집계·조건 등)은 blocked — 이행 보류.
 * 다른 스코프의 동명 키(비 def 표면)는 수집도 보류도 하지 않는다(그대로 유지).
 */
function scanBodyForOutKey(body: ComposerNode[], defKey: string, outKey: string): BodyScan {
  const scan: BodyScan = { blocked: false, cellTargets: [], nodeTargets: [] };
  const block = (): void => {
    scan.blocked = true;
  };
  const checkFormat = (f: BindingFormat | undefined): void => {
    if (formatReferencesKey(f, outKey)) block();
  };
  // 집계/계산 셀 — 집계(over===defKey)의 항목 필드 소비는 위치 무관 검사, 계산 입력은 def 스코프에서만.
  const checkAggregateCompute = (cell: TableCell, inDefScope: boolean): void => {
    const ag = cell.aggregate;
    if (ag) {
      const overHitsDef = ag.over === defKey || ag.over.startsWith(`${defKey}.`);
      if (overHitsDef && (ag.field === outKey || ag.field?.startsWith(`${outKey}.`))) block();
      if ((overHitsDef || inDefScope) && formatReferencesKey(ag.format, outKey)) block();
      if (inDefScope && (ag.over === outKey || ag.over.startsWith(`${outKey}.`))) block();
    }
    if (inDefScope && cell.compute) {
      for (const inp of cell.compute.inputs) {
        if (inp.binding?.bindingKey === outKey) block();
        checkFormat(inp.binding?.format);
      }
      checkFormat(cell.compute.format);
    }
  };

  // def 행 스코프 서브트리 — nested=중첩 행 스코프(mergeRepeatScope 폴백으로 바깥 행 값을 읽을 수
  // 있어 등가성 판단이 불확실 → 발견 시 보류).
  const walkDefScope = (nodes: ComposerNode[], nested: boolean): void => {
    for (const n of nodes) {
      if (n.binding) {
        checkFormat(n.binding.format);
        if (n.binding.bindingKey === outKey) {
          if (nested) block();
          else scan.nodeTargets.push({ nodeId: n.id, binding: n.binding });
        }
      }
      if (n.kind === 'group' || n.kind === 'stack') walkDefScope(n.children, nested);
      else if (n.kind === 'branch') {
        for (const cb of n.conditionBindings ?? []) {
          if (cb.binding.bindingKey === outKey) block();
          checkFormat(cb.binding.format);
        }
        for (const slot of n.branches) {
          // 조건 문자열(evaluateCondition 문법)은 자동 rewrite 금지 — 언급만으로 보류(보수적).
          if (slot.condition?.includes(outKey)) block();
          walkDefScope(slot.children, nested);
        }
      } else if (n.kind === 'repeat') {
        walkDefScope([n.template], true); // 내부 반복 = 중첩 행 스코프
      } else if (n.kind === 'table') {
        const introducesScope = (n.repeatRegions ?? []).length > 0;
        for (const cell of n.cells) {
          checkAggregateCompute(cell, true);
          if (cell.binding) {
            checkFormat(cell.binding.format);
            if (cell.binding.bindingKey === outKey) {
              if (nested || introducesScope) block();
              else
                scan.cellTargets.push({
                  tableId: n.id,
                  row: cell.row,
                  col: cell.col,
                  binding: cell.binding,
                });
            }
          }
          if (cell.content) walkDefScope(cell.content, nested || introducesScope);
        }
      }
    }
  };

  // 상위(비 def 행) 스코프 walk — def 소비 유닛만 찾아 스캔. 다른 스코프의 동명 키는 그대로 둔다.
  const walkTop = (nodes: ComposerNode[]): void => {
    for (const n of nodes) {
      if (n.kind === 'group' || n.kind === 'stack') walkTop(n.children);
      else if (n.kind === 'branch') for (const slot of n.branches) walkTop(slot.children);
      else if (n.kind === 'repeat') {
        if (n.over === defKey) walkDefScope([n.template], false);
        else walkTop([n.template]);
      } else if (n.kind === 'table') {
        const isDefTable = (n.repeatRegions ?? []).some((r) => r.over === defKey);
        for (const cell of n.cells) {
          checkAggregateCompute(cell, isDefTable);
          if (isDefTable) {
            if (cell.binding) {
              checkFormat(cell.binding.format);
              if (cell.binding.bindingKey === outKey) {
                scan.cellTargets.push({
                  tableId: n.id,
                  row: cell.row,
                  col: cell.col,
                  binding: cell.binding,
                });
              }
            }
            // 셀 content 중첩 노드의 스코프 판단은 보류 대상(발견 시만) — 직접 셀 바인딩만 rename.
            if (cell.content) walkDefScope(cell.content, true);
          } else if (cell.content) {
            walkTop(cell.content);
          }
        }
      }
    }
  };
  walkTop(body);
  return scan;
}

/** composerWidgetTree 변환 결과 — blocked면 tree는 폐기(항목 유지). */
interface WidgetTreeResult {
  tree: ComposerWidget[];
  changed: boolean;
  blocked: boolean;
}

/** 배열을 요소 변환해 변경이 있을 때만 새 배열(없으면 원본 참조 유지 — no-op 히스토리 방지). */
function mapKeepRef<T>(list: T[], f: (x: T) => T): T[] {
  let changed = false;
  const next = list.map((x) => {
    const m = f(x);
    if (m !== x) changed = true;
    return m;
  });
  return changed ? next : list;
}

/**
 * composerWidgetTree에서 defKey 소비 표면의 outKey를 newKey로 rename한다.
 *  - repeat 위젯(over===defKey) children 직속 스코프: input bindingKey / labelSuffixFrom /
 *    visibleWhen / enabledWhen, formTable 고정 셀 위젯의 같은 축.
 *  - formTable 반복 행(sourceKey===defKey)의 cellFields[].field(행 필드 flat 읽기 —
 *    composerWidgetTreeRender renderRepeatRow).
 * 등가성이 불확실한 소비(중첩 행 스코프·모달 bindingKey/commit.to·writes·조건 문자열·
 * 소스 키 참조)는 blocked — 이행 보류. 모달 children의 키는 모달 스코프 격리 namespace라
 * 행 필드 참조가 아니므로 하강하지 않는다(프리셋 재매핑 규칙과 동일 스코프 판단).
 */
function renameOutKeyInWidgetTree(
  tree: ComposerWidget[] | undefined,
  defKey: string,
  outKey: string,
  newKey: string,
): WidgetTreeResult {
  if (!tree || tree.length === 0) return { tree: tree ?? [], changed: false, blocked: false };
  let blocked = false;
  const block = (): void => {
    blocked = true;
  };
  // 배열 일괄 writes 대상(`defKey[].outKey…`) — 스코프 무관 실쓰기 참조라 어디서든 보류.
  const batchPrefix = `${defKey}[].`;
  const checkBatchWrites = (w: ComposerWidget): void => {
    if (w.kind !== 'input' || !w.writes) return;
    for (const wr of w.writes) {
      if (!wr.target.startsWith(batchPrefix)) continue;
      const rest = wr.target.slice(batchPrefix.length);
      if (rest === outKey || rest.startsWith(`${outKey}.`)) block();
    }
  };
  // 중첩 스코프 서브트리의 보수적 흔적 검사 — 발견=보류(구조 판단 대신 존재만 본다).
  // 여는 따옴표만 매칭(문자열 값 prefix) — `defKey[].outKey.sub` 같은 하위 경로도 잡는다.
  // 과잉 매칭은 이행 보류(레거시 유지)일 뿐이라 보수적으로 안전.
  const subtreeMentions = (widgets: ComposerWidget[], needle: string): boolean =>
    JSON.stringify(widgets).includes(`"${needle}`);

  // 상위(비 def 행) 스코프의 formTable — 반복 행(sourceKey===defKey) cellFields rename.
  // 고정 셀 위젯은 상위 스코프 읽기라 def 소비가 아니다(일괄 writes만 검사).
  const mapTopFormTable = (w: FormTableWidget): FormTableWidget => {
    for (const row of w.rows) {
      if (row.type !== 'fixed') continue;
      for (const cell of row.cells) if (cell.widget) checkBatchWrites(cell.widget);
    }
    const rows = mapKeepRef(w.rows, (row) => {
      if (row.type !== 'repeat' || row.sourceKey !== defKey) return row;
      const cellFields = mapKeepRef(row.cellFields, (cf) =>
        cf.field === outKey ? { ...cf, field: newKey } : cf,
      );
      return cellFields === row.cellFields ? row : { ...row, cellFields };
    });
    return rows === w.rows ? w : { ...w, rows };
  };

  // def 행 스코프(repeat over===defKey의 children) — 직속 스코프 참조를 rename.
  const mapDefScopeWidget = (w: ComposerWidget): ComposerWidget => {
    checkBatchWrites(w);
    let next: ComposerWidget = w;
    switch (w.kind) {
      case 'input': {
        if ((w.writes ?? []).some((wr) => wr.target === outKey)) block(); // 조립 출력에 쓰기 — 보류
        if (w.optionsSources?.includes(outKey)) block(); // 행 필드를 소스 키로 참조 — 보류
        if (w.sourceFieldMaps && Object.keys(w.sourceFieldMaps).includes(outKey)) block();
        let inp = w;
        if (inp.bindingKey === outKey) inp = { ...inp, bindingKey: newKey };
        if (inp.labelSuffixFrom === outKey) inp = { ...inp, labelSuffixFrom: newKey };
        next = inp;
        break;
      }
      case 'group': {
        const children = mapKeepRef(w.children, mapDefScopeWidget);
        next = children === w.children ? w : { ...w, children };
        break;
      }
      case 'branch': {
        const branches = mapKeepRef(w.branches, (slot) => {
          if (slot.condition?.includes(outKey)) block(); // 조건 문자열 자동 rewrite 금지 — 보류
          const children = mapKeepRef(slot.children, mapDefScopeWidget);
          return children === slot.children ? slot : { ...slot, children };
        });
        next = branches === w.branches ? w : { ...w, branches };
        break;
      }
      case 'repeat': {
        // 중첩 행 스코프 — 서브트리에 outKey 흔적이 있으면 등가성 판단 보류.
        if (subtreeMentions(w.children, outKey)) block();
        break;
      }
      case 'modalTrigger': {
        if (w.bindingKey === outKey) block(); // 모달 UI 상태 저장 키 = 행 필드 — 보류
        const commits = [
          ...(w.purpose?.kind === 'valueCommit' ? w.purpose.commit : []),
          ...(w.commit ?? []),
        ];
        if (commits.some((c) => c.to === outKey)) block(); // 확인 시 행 필드 기록 — 보류
        // children은 모달 스코프 격리 — 미하강(from/필드 매핑은 모달 namespace).
        break;
      }
      case 'formTable': {
        const rows = mapKeepRef(w.rows, (row) => {
          if (row.type === 'repeat') {
            // def 행 스코프 안 반복 행 — 자기 참조(sourceKey===defKey)는 불확실이라 보류,
            // 다른 소스의 내부 행 필드는 별개 namespace(flat 읽기, 폴백 없음)라 무관.
            if (row.sourceKey === defKey) block();
            return row;
          }
          const cells = mapKeepRef(row.cells, (cell) => {
            if (cell.compute) {
              for (const ci of cell.compute.inputs) {
                if (ci.binding?.bindingKey === outKey) block();
              }
              if (formatReferencesKey(cell.compute.format, outKey)) block();
            }
            const cw = cell.widget;
            if (!cw) return cell;
            checkBatchWrites(cw);
            if ((cw.writes ?? []).some((wr) => wr.target === outKey)) block();
            let nw = cw;
            if (nw.bindingKey === outKey) nw = { ...nw, bindingKey: newKey };
            if (nw.labelSuffixFrom === outKey) nw = { ...nw, labelSuffixFrom: newKey };
            if (nw.visibleWhen === outKey) nw = { ...nw, visibleWhen: newKey };
            if (nw.enabledWhen === outKey) nw = { ...nw, enabledWhen: newKey };
            return nw === cw ? cell : { ...cell, widget: nw };
          });
          return cells === row.cells ? row : { ...row, cells };
        });
        next = rows === w.rows ? w : { ...w, rows };
        break;
      }
      default:
        break; // separator/heading/spacer — 값 참조 없음
    }
    // 공통 조건 축(단일 bindingKey) — 행 스코프 값 참조라 함께 rename.
    if (next.visibleWhen === outKey) next = { ...next, visibleWhen: newKey };
    if (next.enabledWhen === outKey) next = { ...next, enabledWhen: newKey };
    return next;
  };

  // 상위 스코프 walk — def 소비 유닛(repeat over===defKey / formTable)만 찾아 변환.
  const mapTopWidget = (w: ComposerWidget): ComposerWidget => {
    checkBatchWrites(w);
    switch (w.kind) {
      case 'group': {
        const children = mapKeepRef(w.children, mapTopWidget);
        return children === w.children ? w : { ...w, children };
      }
      case 'branch': {
        const branches = mapKeepRef(w.branches, (slot) => {
          const children = mapKeepRef(slot.children, mapTopWidget);
          return children === slot.children ? slot : { ...slot, children };
        });
        return branches === w.branches ? w : { ...w, branches };
      }
      case 'modalTrigger': {
        // 모달 스코프는 격리 namespace — def 행 소비가 아니므로 rename 하강 없음.
        // 단 배열 일괄 writes(`defKey[].outKey`)는 실쓰기 참조라 흔적만 보수 검사.
        if (subtreeMentions(w.children, `${batchPrefix}${outKey}`)) block();
        return w;
      }
      case 'repeat': {
        if (w.over === defKey) {
          const children = mapKeepRef(w.children, mapDefScopeWidget);
          return children === w.children ? w : { ...w, children };
        }
        // 다른 데이터의 행 스코프 — def 소비 아님. 일괄 writes 흔적만 보수 검사.
        if (subtreeMentions(w.children, `${batchPrefix}${outKey}`)) block();
        return w;
      }
      case 'formTable':
        return mapTopFormTable(w);
      default:
        return w;
    }
  };

  const mapped = mapKeepRef(tree, mapTopWidget);
  return { tree: mapped, changed: mapped !== tree, blocked };
}

/**
 * 레거시 mergeFields의 결정적 자동 이행(순수 변환).
 *
 * 대상: `compute.assembler==='join-by-key'` && `inputs.join` 존재 && mergeFields 비어있지 않은
 * dataDef. 각 top-level 항목(outKey ← srcPath)의 def 스코프 바인딩을 `${inputs.join}.${srcPath}`
 * 접두 키로 rename한 뒤 항목을 제거한다. 전부 이행되면 options에서 mergeFields 키 자체를
 * 제거한다(빈 객체 잔재 금지). 이행 불가 항목(dotted srcPath·불확실 소비)은 원형 유지 —
 * 레거시 경로가 계속 동작하고 요약 UI(DataDefJoinConfigEditor)에 잔존 표시된다.
 * 변경이 없으면 원본 참조를 그대로 반환한다(no-op 히스토리 방지 — rowsColumnOps 선례).
 */
export function migrateLegacyMergeFields(t: ReportTemplate): ReportTemplate {
  const defs = t.dataDefs;
  if (!defs || defs.length === 0) return t;
  let next = t;
  for (let i = 0; i < defs.length; i += 1) {
    const def = defs[i];
    if (!isJoinByKeyDef(def)) continue;
    const compute = def.compute;
    const rawMergeFields = compute?.options?.mergeFields;
    if (!compute || !rawMergeFields || typeof rawMergeFields !== 'object') continue;
    if (Array.isArray(rawMergeFields)) continue;
    const join = readJoinConfig(def).join;
    if (!join) continue; // 조인 소스 미지정 — 접두 키 산출 자체가 없어 이행 불가
    const entries = readMergeFieldEntries(def);
    if (entries.length === 0) continue;

    const migrated = new Set<string>();
    for (const { outKey, srcPath } of entries) {
      // dotted-path는 접두 flat 키가 산출하지 않는다(top-level 칼럼만) — 항목 유지.
      if (!srcPath || srcPath.includes('.')) continue;
      const newKey = `${join}.${srcPath}`;
      const bodyScan = scanBodyForOutKey(next.body, def.key, outKey);
      const wt = renameOutKeyInWidgetTree(next.composerWidgetTree, def.key, outKey, newKey);
      if (bodyScan.blocked || wt.blocked) continue; // 불확실 소비 발견 — 이행 보류(레거시 유지)
      let applied = next;
      for (const tg of bodyScan.cellTargets) {
        applied = updateCell(applied, tg.tableId, tg.row, tg.col, {
          binding: { ...tg.binding, bindingKey: newKey },
        });
      }
      for (const tg of bodyScan.nodeTargets) {
        applied = updateNode(applied, tg.nodeId, {
          binding: { ...tg.binding, bindingKey: newKey },
        });
      }
      if (wt.changed) applied = { ...applied, composerWidgetTree: wt.tree };
      next = applied;
      migrated.add(outKey);
    }
    if (migrated.size === 0) continue;

    // 이행된 항목만 제거 — 보류 항목·문자열 아닌(잘못 저작) 값은 원형 그대로 보존.
    const raw = rawMergeFields as Record<string, unknown>;
    const keep: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw)) if (!migrated.has(k)) keep[k] = v;
    const options: Record<string, unknown> = { ...compute.options };
    if (Object.keys(keep).length > 0) options.mergeFields = keep;
    else delete options.mergeFields; // 전부 이행 — 빈 객체 잔재 금지
    const nextDef: DataDef = { ...def, compute: { ...compute, options } };
    const arr = next.dataDefs ?? defs;
    next = { ...next, dataDefs: arr.map((d, j) => (j === i ? nextDef : d)) };
  }
  return next;
}
